import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { IcnAlert, IcnCheck, IcnClose, IcnDoc, IcnUpload } from '../../icons/index.js';
import { Badge, type BadgeTone } from '../Badge/index.js';
import { Button } from '../Button/index.js';
import { IconButton } from '../IconButton/index.js';
import { Progress, type ProgressTone } from '../Progress/index.js';
import { formatFileSize } from '../../utils/index.js';

type FileUploadStatus = NonNullable<FileUploadItem['status']>;

export interface FileUploadHandle {
  open: () => void;
  focus: () => void;
}

export interface FileUploadProps {
  /** MIME filter, like `<input accept>`. Example: `"image/*,.pdf"`. */
  accept?: string;
  /** Allow selecting multiple files. Default true. */
  multiple?: boolean;
  /** Enable browser folder selection where supported. */
  directory?: boolean;
  /** Disable browser drop to prevent accidental navigation when dragging
   *  outside the zone. Consumers can set `false` to allow drop anywhere. */
  captureGlobalDrop?: boolean;
  /** Max size per file in bytes. Files larger are flagged as invalid. */
  maxSize?: number;
  /** Fires once per drop / picker close with the resulting files
   *  (after accept + maxSize filters applied). */
  onFiles?: (files: ReadonlyArray<File>) => void;
  /** Overrides the internal file list display — useful when upload
   *  progress / status comes from an external queue. */
  files?: ReadonlyArray<FileUploadItem>;
  /** Remove handler. Receives the file's `name + size + lastModified`
   *  tuple so keys stay stable even when rapid re-adds happen. */
  onRemove?: (id: string) => void;
  /** Labels (pre-translated). */
  labels?: {
    dropHint?: string;
    description?: string;
    browse?: string;
    orText?: string;
    remove?: string;
    inputLabel?: string;
    queueLabel?: string;
    clear?: string;
    progressLabel?: string;
    statusQueued?: string;
    statusUploading?: string;
    statusDone?: string;
    statusError?: string;
  };
  onClear?: () => void;
  disabled?: boolean;
  className?: string;
  ref?: Ref<FileUploadHandle>;
}

export interface FileUploadItem {
  id: string;
  name: string;
  size: number;
  /** 0..1 progress. Omit to hide bar. */
  progress?: number;
  /** Status chip. */
  status?: 'queued' | 'uploading' | 'done' | 'error';
  error?: string;
}

function fileId(f: File): string {
  return `${f.name}-${String(f.size)}-${String(f.lastModified)}`;
}

function statusText(status: FileUploadStatus, labels: FileUploadProps['labels']): string {
  switch (status) {
    case 'queued':
      return labels?.statusQueued ?? 'Queued';
    case 'uploading':
      return labels?.statusUploading ?? 'Uploading';
    case 'done':
      return labels?.statusDone ?? 'Done';
    case 'error':
      return labels?.statusError ?? 'Failed';
  }
}

function statusTone(status: FileUploadStatus): BadgeTone {
  switch (status) {
    case 'done':
      return 'success';
    case 'error':
      return 'danger';
    case 'uploading':
      return 'info';
    case 'queued':
      return 'neutral';
  }
}

function progressTone(status: FileUploadStatus): ProgressTone {
  switch (status) {
    case 'done':
      return 'success';
    case 'error':
      return 'danger';
    default:
      return 'accent';
  }
}

function normaliseProgress(file: FileUploadItem): number {
  if (file.status === 'done') return 100;
  const progress = file.progress ?? 0;
  return Math.min(100, Math.max(0, progress * 100));
}

function overallProgress(files: ReadonlyArray<FileUploadItem>): number {
  if (files.length === 0) return 0;
  const totalWeight = files.reduce((sum, file) => sum + Math.max(1, file.size), 0);
  const weighted = files.reduce(
    (sum, file) => sum + Math.max(1, file.size) * normaliseProgress(file),
    0,
  );
  return weighted / totalWeight;
}

function statusIcon(status: FileUploadStatus): ReactNode {
  if (status === 'done') return <IcnCheck size={16} active />;
  if (status === 'error') return <IcnAlert size={16} active />;
  if (status === 'uploading') return <IcnUpload size={16} active />;
  return <IcnDoc size={16} />;
}

/** Drag-and-drop file upload zone.
 *
 *  Structure:
 *   ┌──────────────────────────────────────┐
 *   │ ⬆  Drop files here, or [Browse]       │
 *   └──────────────────────────────────────┘
 *     • foo.png   120 KB     [━━━━━■■■] 62%    ✕
 *     • bar.pdf   4.2 MB                       ✓
 *
 *  The outer zone is a drop target + click-to-open-picker (via a hidden
 *  <input type=file>). When the user drags files over the whole window,
 *  every dropzone picks up the dragover state to hint receptiveness
 *  (with `captureGlobalDrop`). Files are filtered by `accept` + `maxSize`
 *  before being reported via `onFiles`.
 *
 *  Internal state is dumb: this primitive holds "what's shown in the
 *  list" only when the consumer doesn't pass `files` — it's not an
 *  upload manager. Pair with your own fetch/xhr + progress callbacks. */
export function FileUpload(props: FileUploadProps): ReactNode {
  const {
    accept,
    multiple = true,
    directory = false,
    maxSize,
    onFiles,
    files: controlledFiles,
    onRemove,
    onClear,
    labels,
    disabled = false,
    className,
    ref,
  } = props;

  const [dragging, setDragging] = useState(false);
  const [internal, setInternal] = useState<ReadonlyArray<FileUploadItem>>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const browseRef = useRef<HTMLButtonElement>(null);

  const files = controlledFiles ?? internal;
  const allowMultiple = multiple || directory;
  const doneCount = files.filter((file) => file.status === 'done').length;
  const finishedCount = files.filter(
    (file) => file.status === 'done' || file.status === 'error',
  ).length;
  const progress = overallProgress(files);
  const canClear = onClear !== undefined && finishedCount > 0;

  const accepted = useCallback(
    (f: File): boolean => {
      if (maxSize !== undefined && f.size > maxSize) return false;
      if (accept === undefined || accept.trim() === '') return true;
      const patterns = accept.split(',').map((s) => s.trim().toLowerCase());
      const name = f.name.toLowerCase();
      const type = f.type.toLowerCase();
      return patterns.some((p) => {
        if (p === '') return false;
        if (p.startsWith('.')) return name.endsWith(p);
        if (p.endsWith('/*')) return type.startsWith(p.slice(0, p.length - 1));
        return type === p;
      });
    },
    [accept, maxSize],
  );

  const openPicker = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  useImperativeHandle(
    ref,
    () => ({
      open: openPicker,
      focus: () => {
        browseRef.current?.focus();
      },
    }),
    [openPicker],
  );

  useEffect(() => {
    const input = inputRef.current;
    if (input === null) return;
    if (directory) {
      input.setAttribute('webkitdirectory', '');
      input.setAttribute('directory', '');
      return;
    }
    input.removeAttribute('webkitdirectory');
    input.removeAttribute('directory');
  }, [directory]);

  const handleFiles = useCallback(
    (incoming: FileList | null) => {
      if (incoming === null || disabled) return;
      const arr: File[] = [];
      for (let i = 0; i < incoming.length; i++) {
        const f = incoming.item(i);
        if (f !== null && accepted(f)) arr.push(f);
        if (!allowMultiple && arr.length > 0) break;
      }
      if (controlledFiles === undefined) {
        const items: FileUploadItem[] = arr.map((f) => ({
          id: fileId(f),
          name: f.name,
          size: f.size,
          status: 'queued',
        }));
        setInternal((prev) => (allowMultiple ? [...prev, ...items] : items));
      }
      onFiles?.(arr);
    },
    [accepted, allowMultiple, disabled, controlledFiles, onFiles],
  );

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      /* Reset the native control so re-adding the same file re-triggers
       * change. */
      e.target.value = '';
    },
    [handleFiles],
  );

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);
      if (disabled) return;
      /* In iframe / restricted contexts, dataTransfer can be null and
       * accessing `.files` throws. Guard and bail quietly. */
      const dt = e.dataTransfer;
      /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */
      if (dt === null || dt === undefined) return;
      handleFiles(dt.files);
    },
    [disabled, handleFiles],
  );

  const onDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!disabled) setDragging(true);
    },
    [disabled],
  );

  const onDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    /* Only hide drag state when leaving the zone itself (not a child). */
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setDragging(false);
  }, []);

  const removeById = useCallback(
    (id: string) => {
      if (controlledFiles === undefined) {
        setInternal((prev) => prev.filter((it) => it.id !== id));
      }
      onRemove?.(id);
    },
    [controlledFiles, onRemove],
  );

  const l = labels ?? {};
  const classes = [
    'vq-file-upload',
    dragging && 'vq-file-upload--dragging',
    disabled && 'vq-file-upload--disabled',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} data-component="FileUpload">
      <div
        className="vq-file-upload__zone"
        aria-disabled={disabled}
        aria-label={l.dropHint ?? 'Drop files here or click to browse'}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onClick={(event) => {
          const target = event.target;
          if (target instanceof Element && target.closest('button') !== null) return;
          openPicker();
        }}
      >
        <span className="vq-file-upload__glyph" aria-hidden="true">
          <IcnUpload size={22} active={dragging} />
        </span>
        <span className="vq-file-upload__copy">
          <span className="vq-file-upload__primary">{l.dropHint ?? 'Drop files here'}</span>
          <span className="vq-file-upload__secondary">
            {l.description ?? l.orText ?? 'Choose files or drop them onto this area.'}
          </span>
        </span>
        <Button
          ref={browseRef}
          variant="secondary"
          size="sm"
          iconLeft={<IcnUpload size={14} />}
          disabled={disabled}
          onClick={openPicker}
        >
          {l.browse ?? 'Browse files'}
        </Button>
        <input
          ref={inputRef}
          type="file"
          className="vq-file-upload__input"
          accept={accept}
          multiple={allowMultiple}
          onChange={onChange}
          disabled={disabled}
          tabIndex={-1}
          aria-label={l.inputLabel}
          aria-hidden={l.inputLabel === undefined ? 'true' : undefined}
        />
      </div>
      {files.length > 0 && (
        <div className="vq-file-upload__queue" aria-label={l.queueLabel ?? 'Upload queue'}>
          <div className="vq-file-upload__queue-head">
            <span className="vq-file-upload__queue-title">
              {l.queueLabel ?? 'Upload queue'}
            </span>
            <Badge tone="neutral" size="sm" numeric>
              {String(doneCount)}/{String(files.length)}
            </Badge>
            <Progress
              className="vq-file-upload__overall"
              value={progress}
              size="sm"
              label={l.progressLabel ?? 'Upload progress'}
            />
            {onClear !== undefined ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={!canClear}
                onClick={onClear}
              >
                {l.clear ?? 'Clear finished'}
              </Button>
            ) : null}
          </div>
          <ul className="vq-file-upload__list">
            {files.map((f) => {
              const status = f.status ?? 'queued';
              const pct = normaliseProgress(f);
              const removable = status === 'done' || status === 'error';
              return (
                <li key={f.id} className="vq-file-upload__item" data-status={status}>
                  <span className="vq-file-upload__item-icon" data-status={status} aria-hidden>
                    {statusIcon(status)}
                  </span>
                  <span className="vq-file-upload__file">
                    <span className="vq-file-upload__name" title={f.name}>
                      {f.name}
                    </span>
                    <span className="vq-file-upload__size">{formatFileSize(f.size)}</span>
                    {f.error !== undefined && f.error !== '' ? (
                      <span className="vq-file-upload__error">{f.error}</span>
                    ) : null}
                  </span>
                  <span className="vq-file-upload__progress-cell">
                    <Progress
                      value={pct}
                      tone={progressTone(status)}
                      size="sm"
                      label={`${f.name} ${statusText(status, labels)}`}
                    />
                    <span className="vq-file-upload__percent">{Math.round(pct)}%</span>
                  </span>
                  <Badge tone={statusTone(status)} size="sm" dot={status === 'uploading'}>
                    {statusText(status, labels)}
                  </Badge>
                  <IconButton
                    label={l.remove ?? `Remove ${f.name}`}
                    size="sm"
                    variant="ghost"
                    disabled={!removable}
                    onClick={() => {
                      removeById(f.id);
                    }}
                  >
                    <IcnClose size={12} />
                  </IconButton>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
